import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Shield, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Team & Roles</h1>
          <p className="text-muted-foreground mt-1">Manage who can access the system.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="bg-sky-500 hover:bg-sky-600 text-white gap-2">
              <Plus className="h-4 w-4" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input data-testid="user-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="user-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input data-testid="user-password-input" type="text" required value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff (log entries)</SelectItem>
                    <SelectItem value="admin">Admin (full control + approvals)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="user-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white">Create User</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-sky-500/5 border-sky-500/20">
          <CardContent className="p-5">
            <Shield className="h-6 w-6 text-sky-400 mb-2" />
            <h3 className="font-semibold">Admin</h3>
            <p className="text-sm text-muted-foreground mt-1">Full access — approve/reject gate passes, manage users & items, delete records.</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-500/5 border-slate-500/20">
          <CardContent className="p-5">
            <UserIcon className="h-6 w-6 text-slate-400 mb-2" />
            <h3 className="font-semibold">Staff</h3>
            <p className="text-sm text-muted-foreground mt-1">Log expenses, inward/outward entries and create gate passes (pending approval).</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border/50 hover:bg-white/5">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={u.role === "admin" ? "text-sky-300 border-sky-500/30" : "text-slate-300 border-slate-500/30"}>
                      {u.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== user?.id && (
                      <Button size="icon" variant="ghost" data-testid={`user-delete-${u.id}`} onClick={() => del(u.id)}
                        className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
